import React, { useState } from 'react';
import { Card, Input, Typography, Row, Col, Space, message } from 'antd';
import { ThunderboltOutlined, ClearOutlined } from '@ant-design/icons';
import { Sparkles } from 'lucide-react';

import { MenuSections } from '../types/menu-sections';
import { tokens } from '../theme/tokens';
import { useBanners } from '../context/banners';
import { useModal } from '../context/modal';
import { useCreatePromptBanner } from '../hooks/banners/creation/prompt/use-create-prompt-banner';
import { useClientId } from '../hooks/use-client-id';
import { useResponsiveSizes } from '../hooks/use-responsive-sizes';

import { TemplateCard } from '../components/template-card';
import { BrandButton, SubtleButton, ChipButton } from '../components/UI/buttons';
import { getCreativityOptions } from '../constants/clients/registry';
import { ResponsiveSelect } from '../components/UI/inputs/responsive-select';
import { getClientOptions } from '../constants/clients/client-options';

const { Title, Text } = Typography;
const { TextArea } = Input;

const TEST_CLIENT_ID = 'TEST';

interface CreateBannerPageProps {
  onNavigate?: (section: MenuSections) => void;
}

export const CreateBannerPage: React.FC<CreateBannerPageProps> = ({ onNavigate }) => {
  const { fontSize, screens, spacing } = useResponsiveSizes();
  const { createPromptBanner } = useCreatePromptBanner();
  const { refreshAllBanners } = useBanners();
  const { openAsset } = useModal();

  const [prompt, setPrompt] = useState('');
  const [selectedClient, setSelectedClient] = useState<string | undefined>();
  const [isGenerating, setIsGenerating] = useState(false);

  const clientId = useClientId();
  const isTestUser = clientId === TEST_CLIENT_ID;
  const availableTemplates = getCreativityOptions(clientId);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    // Si es usuario TEST y no seleccionó cliente, mostrar error
    if (isTestUser && !selectedClient) {
      message.error('Por favor selecciona un cliente');
      return;
    }

    setIsGenerating(true);

    // Solo pasar clientName si es usuario TEST
    const result = await createPromptBanner(prompt, isTestUser ? selectedClient : undefined);

    if (result) {
      message.success('Procesando tu solicitud...');
      await refreshAllBanners();

      openAsset(result.bannerId);

      if (onNavigate) {
        onNavigate('dashboard');
      }
    } else {
      message.error('No se pudo procesar tu solicitud. Intentá nuevamente.');
    }

    setIsGenerating(false);
  };

  const handleClear = () => {
    setPrompt('');
    setSelectedClient(undefined);
  };

  const handlePredefinedPrompt = (selectedPrompt: string) => {
    setPrompt(selectedPrompt);
  };

  return (
    <div style={{ maxWidth: '100%', margin: `0 ${spacing.xxl}px` }}>
      <Title style={{ fontSize: fontSize.xxxl, marginBottom: spacing.xs, display: 'flex' }}>Creá tu template</Title>
      <Text type='secondary' style={{ fontSize: fontSize.xl, marginBottom: 32, display: 'flex' }}>
        Elegí un diseño base para tu publicidad
      </Text>

      <Card>
        <Title
          style={{
            marginBottom: 8,
            marginTop: 0,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            fontSize: screens.xs ? fontSize.xl : fontSize.xxl
          }}
        >
          <Sparkles size={20} color={tokens.colors.brands.blue[600]} />
          Crear asset
        </Title>

        <Text type='secondary' style={{ marginBottom: 16, display: 'flex', fontSize: fontSize.lg }}>
          Describí tu idea y la IA generará opciones para vos
        </Text>

        {/* Dropdown de cliente (solo para TEST) */}
        {isTestUser && (
          <div style={{ marginBottom: 16, display: 'flex', gap: 15, alignItems: 'center', fontSize: fontSize.base }}>
            <Text strong style={{ display: 'block', fontSize: fontSize.base }}>
              Cliente del template:
            </Text>
            <ResponsiveSelect
              placeholder='Seleccionar cliente'
              value={selectedClient}
              onChange={setSelectedClient}
              options={getClientOptions()}
            />
          </div>
        )}

        <Row gutter={16}>
          <Col flex='auto'>
            <TextArea
              placeholder='Ej: Necesito un video para una campaña de Black Friday con colores oscuros y llamativo...'
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              rows={4}
              disabled={isGenerating}
              style={{ fontSize: fontSize.base }}
            />
          </Col>
          <Col>
            <div
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-evenly'
              }}
            >
              <BrandButton
                icon={<ThunderboltOutlined />}
                onClick={handleGenerate}
                loading={isGenerating}
                disabled={!prompt.trim()}
              >
                {isGenerating ? 'Creando...' : 'Generar'}
              </BrandButton>

              <SubtleButton
                icon={<ClearOutlined />}
                onClick={handleClear}
                disabled={!prompt.trim()}
                showShimmer={prompt.trim().length > 0}
              >
                Limpiar
              </SubtleButton>
            </div>
          </Col>
        </Row>

        <div style={{ marginTop: 16 }}>
          <Text strong style={{ marginBottom: 8, display: 'flex', fontSize: fontSize.base }}>
            Ejemplos rápidos:
          </Text>
          <Space wrap style={{ display: 'flex' }}>
            {availableTemplates
              .filter(t => !t.disabled)
              .map(template => (
                <ChipButton key={template.id} onClick={() => handlePredefinedPrompt(template.promptTemplate!)}>
                  {template.title}
                </ChipButton>
              ))}
          </Space>
        </div>
      </Card>

      <div style={{ margin: '32px 0' }}>
        <Text
          style={{
            fontSize: fontSize.xl,
            fontWeight: 600,
            marginBottom: 24,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          Elegí un template prediseñado
        </Text>

        {/* Grid con dense packing */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: screens.xs ? 12 : screens.sm ? 16 : 20,
            width: '100%',
            justifyContent: 'flex-between'
          }}
        >
          {availableTemplates.map(template => (
            <div key={template.id}>
              {template.disabled && template.video ? (
                <div>
                  <TemplateCard
                    title={template.title}
                    onClick={() => {}}
                    disabled={template.disabled}
                    image={template.optimized_image}
                    video={template.video}
                    aspectRatio={template.aspectRatio}
                  />
                </div>
              ) : (
                <TemplateCard
                  title={template.title}
                  onClick={() => {
                    const encodedId = encodeURIComponent(template.id);
                    onNavigate?.(`creativity-form/${encodedId}`);
                  }}
                  disabled={template.disabled}
                  image={template.image}
                  aspectRatio={template.aspectRatio}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
